<template>
    <div class="">
        <div v-if="isLoading" class="d-flex justify-content-center mt-5 mb-1">
            <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">{{ getTranslatedText('__loading__') }}</span>
            </div>
        </div>
        <!-- Both messages stay up for a few seconds, then the form moves on by itself. text-center (not a
             flex wrapper) so a message that wraps on a phone stays centred under the step title. -->
        <template v-if="!!isPhoneVerified">
            <div class="text-center my-2">
                <svg class="text-success mb-2" width="40" height="40" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                    <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0m-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
                </svg>
                <h4>{{ getTranslatedText('__verified_success__', 'components.smsverification') }}</h4>
                <ContinuingNotice />
            </div>
        </template>
        <template v-else>
            <div v-if="exitPhoneVerification" class="text-center my-2">
                <h4>{{ getTranslatedText('__max_attempts_reached__', 'components.smsverification') }}</h4>
                <ContinuingNotice />
            </div>
            <div v-else>
                <div v-if="errorMessage && errorMessage.length" class="row my-2">
                    <div class="col-md-12 text-center">
                        <h5 class="text-danger">{{ getTranslatedText(errorMessage, 'components.smsverification') }}</h5>
                    </div>
                </div>
                <div v-if="!requestedVerificationCode">
                    <div class="row">
                        <div class="col-md-12 text-center">
                            <template v-if="!hideSubtitles">
                                <template v-if="app.product_name === 'car_loan'">
                                    <small style="display: inline-block; margin-bottom:20px;">{{ getTranslatedText('__verification_code_sent__', 'components.smsverification') }}</small>
                                </template>
                                <template v-else>
                                    <p>{{ getTranslatedText('__new_accounts_must_verify__', 'components.smsverification') }}</p>
                                </template>
                            </template>
                        </div>
                        <div v-if="!reachedMaxAttempts" class="col-md-12 text-center mb-1">
                            <small class="text-muted">
                                <span v-if="isLastAttempt">
                                    {{ getTranslatedText('__last_attempt__', 'components.smsverification') }}
                                </span>
                            </small>
                        </div>
                        <div class="col-md-6 offset-md-3">
                            <input
                                class="form-control"
                                minlength="10"
                                maxlength="20"
                                inputmode="tel"
                                :placeholder="getTranslatedText('__enter_phone__', 'components.smsverification')"
                                :aria-label="getTranslatedText('__enter_phone__', 'components.smsverification')"
                                :value="verificationPhoneNumber"
                                @input="inputPhoneNumber"
                                @keydown.enter.prevent="requestVerificationCode()"
                            />
                            <div v-show="validationMessage && validationMessage.length" class="text-danger"><ul class="text-danger mb-0 mt-2"><li><span v-text="getTranslatedText(validationMessage, 'components.smsverification')"></span></li></ul></div>
                        </div>
                    </div>
                    <div class="row mt-3">
                        <div class="col-md-6 offset-md-3" style="text-align: center;">
                            <a v-keyboard-button @click.prevent="requestVerificationCode()" class="btn btn-lg w-100 btn-primary" :class="{ disabled: isLoading }" :aria-disabled="isLoading ? 'true' : null">{{ getTranslatedText( verificationCodeText, 'buttons') }}</a>
                        </div>
                    </div>
                </div>
                <div v-else>
                    <div class="row">
                        <div v-if="!reachedMaxAttempts" class="col-md-12 text-center mb-3">
                            <small class="text-muted">
                                {{ getTranslatedText('__sent_sms_to__', 'components.smsverification') }}
                                <b>{{ verificationPhoneNumber }}</b>.
                                <a v-if="showResendCode && !hasReachedMaxAttempts('code')" href="#" @click.prevent="editPhoneNumber()">
                                    {{ getTranslatedText('__edit_phone__', 'components.smsverification') }}</a><br>
                                <span v-if="showResendCode && isLastAttempt">
                                    {{ getTranslatedText('__last_attempt__', 'components.smsverification') }}
                                </span>
                            </small>
                        </div>
                        <div class="col-md-8 offset-md-2">
                            <input
                                class="form-control"
                                minlength="4"
                                maxlength="6"
                                inputmode="numeric"
                                autocomplete="one-time-code"
                                :placeholder="getTranslatedText('__enter_code__', 'components.smsverification')"
                                :aria-label="getTranslatedText('__enter_code__', 'components.smsverification')"
                                v-model="verificationCode"
                                @input="onCodeInput"
                                @keydown.enter.prevent="getVerification()"
                            />
                            <div v-show="validationMessage && validationMessage.length" class="text-danger"><ul class="text-danger mb-0 mt-2"><li><span v-text="getTranslatedText(validationMessage, 'components.smsverification')"></span></li></ul></div>
                        </div>
                        <div v-if="resendCodeAttempts < maxResendCodeAttempts && !hasReachedMaxAttempts('code')" class="col-md-12 mt-3 text-center">
                            <small class="text-muted">
                                <div v-if="!canResendCode" class="">
                                    {{ getTranslatedText('__resend_code_in__', 'components.smsverification') }} {{ nextAttemptTimer }} {{ getTranslatedText('__seconds__', 'components.smsverification') }}.
                                </div>
                                <div v-else class="">
                                    <a href="#" @click.prevent="resendCode()">{{ getTranslatedText('__resend_code__', 'components.smsverification') }}</a>
                                </div>
                            </small>
                        </div>
                    </div>
                    <div class="row mt-3">
                        <div class="col-md-6 offset-md-3" style="text-align: center;">
                            <a v-keyboard-button @click.prevent="getVerification()" class="btn btn-lg w-100 btn-primary" :class="{ disabled: isLoading }" :aria-disabled="isLoading ? 'true' : null">{{getTranslatedText('__verify_code__', 'components.smsverification')}}</a>
                        </div>
                    </div>
                </div>

                <div v-if="allowSkipVerification || showSkipVerification" class="row mt-3">
                    <div class="col-md-12 py-2 text-center">
                        <template v-if="app.product_name === 'car_loan'">
                            <div v-html="getTranslatedText('__skip_verification_car_loan__', 'components.smsverification')"></div>
                        </template>
                        <template v-else>
                            <div v-html="getTranslatedText('__skip_verification_member_enrollment__', 'components.smsverification')"></div>
                        </template>
                    </div>
                    <div class="col-md-4 offset-md-4" style="text-align: center;">
                        <a v-keyboard-button id="btnNext" class="btn btn-md w-100 btn-secondary" disabled="false" @click="$emit('goToNextStep')">
                            {{ getTranslatedText('__skip__', 'buttons') }}
                        </a>
                    </div>
                </div>

            </div>
        </template>
    </div>
</template>
<script setup>

import { ref, computed, inject, onMounted, onBeforeUnmount } from 'vue';
import { canadianPhoneValidation, usPhoneValidation } from "@/form/helpers/field-validations.js";
import { phoneDigits } from "@/form/helpers/input-formats.js";
import { vKeyboardButton } from '@/directives/keyboard-button.js';
import { useLoadingState } from '@/composables/useLoadingState.js';
import { useAttemptLimit } from '@/composables/useAttemptLimit.js';
import { postAndReturnData } from '@/utils/api.js';
import { getBaseUrl } from '@/utils/url.js';
import { useCountdownTimer } from '@/composables/useCountdownTimer.js';
import ContinuingNotice from '@/components/form/ContinuingNotice.vue';

const MAX_RESEND_ATTEMPTS = 2;
const SUCCESS_REDIRECT_DELAY = 2.5;
const MAX_ATTEMPTS_REDIRECT_DELAY = 3;
const COUNTDOWN_SECONDS = 60;

const pendingTimers = [];
function safeDelayedCall(fn, seconds) {
    pendingTimers.push(setTimeout(fn, seconds * 1000));
}
onBeforeUnmount(() => pendingTimers.forEach(clearTimeout));

defineProps({
    name: { type: String, required: true },
    hideSubtitles: { type: Boolean, default: false },
});
const emit = defineEmits(["goToNextStep"]);

const { app, updateApplicationField, getTranslatedText } = inject('app');

// --- Refs ---

const { isLoading, errorMessage, validationMessage, clearMessages } = useLoadingState();
const allowSkipVerification = ref(false);
const requestedVerificationCode = ref(false);
const currentStep = ref('code');
const { counts: attemptsCount, hasReachedMax: hasReachedMaxAttempts, isLastAttempt: isLastAttemptFor, increment: incrementAttempt } = useAttemptLimit({ code: 3, verification: 3 });
const verificationCodeText = ref('__request_code__');
const verificationPhoneNumber = ref('');
const verificationCode = ref('');
const resendCodeAttempts = ref(0);
const maxResendCodeAttempts = MAX_RESEND_ATTEMPTS;
const showResendCode = ref(false);
const locationUrl = getBaseUrl();
const isPhoneVerified = ref(false);
const exitPhoneVerification = ref(false);
const sid = ref(null);
const { remaining: nextAttemptTimer, start: startCountdown } = useCountdownTimer();

// --- Computed ---

const showSkipVerification = computed(() => {
    if (app.value.forceSmsVerification) {
        return false;
    }
    const minAttemptsBeforeShowingSkip = 1;
    return (currentStep.value === 'code' && attemptsCount.value.code >= minAttemptsBeforeShowingSkip) ||
        (currentStep.value === 'verification' && (attemptsCount.value.code >= minAttemptsBeforeShowingSkip || attemptsCount.value.verification >= minAttemptsBeforeShowingSkip));
});

const reachedMaxAttempts = computed(() => hasReachedMaxAttempts(currentStep.value));

const canResendCode = computed(() => {
    return nextAttemptTimer.value <= 0;
});

const isLastAttempt = computed(() => isLastAttemptFor(currentStep.value));

// --- Methods ---

function nextStepAfterSeconds(seconds = 2) {
    if (!!app.value.forceSmsVerification && !isPhoneVerified.value) {
        window.location.href = '/thank-you';
        return;
    }
    if (app.value.forcePhoneVerification && !isPhoneVerified.value) {
        updateApplicationField('wants_to_create_account', "no");
        safeDelayedCall(() => emit('goToNextStep'), seconds);
    }
}

async function requestVerificationCode() {
    // A second tap while the request is out would send another SMS and use another attempt.
    if (isLoading.value) {
        return;
    }
    clearMessages();

    if (!validateInput('phone')) {
        validationMessage.value = '__enter_valid_phone__';
        return;
    }

    incrementAttempt('code');

    const codeRequest = await smsVerificationRequest('code', {
        phone: verificationPhoneNumber.value,
        source: locationUrl,
        first_name: app.value.first_name || '',
        last_name: app.value.last_name || '',
        email: app.value.email || '',
        application_address: [
            [app.value.address?.streetNumber, app.value.address?.street].filter(Boolean).join(' '),
            app.value.address?.city,
            app.value.address?.province,
            app.value.address?.postalCode,
        ].filter(Boolean).join(', ') || null,
    });
    if (codeRequest && codeRequest.status === 'success' && codeRequest.sid && codeRequest.sid.length) {
        requestedVerificationCode.value = true;
        currentStep.value = "verification";
        sid.value = codeRequest.sid;
        resetAutoVerify();
        reinitializeTimer();
    } else {
        errorMessage.value = "__sms_code_could_not_send__";
        sid.value = null;
        checkAttemptsLimits();
        return;
    }

    showResendCode.value = true;
}

async function getVerification() {
    // A second tap (or a tap right after an auto-verify) would use another of the 3 attempts.
    if (isLoading.value) {
        return;
    }
    clearMessages();

    if (!validateInput('code')) {
        validationMessage.value = '__enter_valid_code__';
        return;
    }

    incrementAttempt('verification');

    requestedVerificationCode.value = true;
    currentStep.value = "verification";
    verificationCodeText.value = '__request_code__';

    const verificationRequest = await smsVerificationRequest('verification', {
        sid: sid.value,
        code: verificationCode.value,
        source: locationUrl,
        first_name: app.value.first_name || '',
        last_name: app.value.last_name || '',
        email: app.value.email || '',
        phone: verificationPhoneNumber.value,
        application_address: [
            [app.value.address?.streetNumber, app.value.address?.street].filter(Boolean).join(' '),
            app.value.address?.city,
            app.value.address?.province,
            app.value.address?.postalCode,
        ].filter(Boolean).join(', ') || null,
    });
    if (verificationRequest && verificationRequest.status === 'success') {
        successfulPhoneVerification();
    } else {
        errorMessage.value = '__sms_code_is_incorrect__';
        checkAttemptsLimits();
    }
}

function successfulPhoneVerification() {
    isPhoneVerified.value = true;
    updateApplicationField('verified_phone_number', verificationPhoneNumber.value);
    updateApplicationField('phone_number', verificationPhoneNumber.value);
    updateApplicationField('isPhoneVerified', true);
    safeDelayedCall(() => emit('goToNextStep'), SUCCESS_REDIRECT_DELAY);
}

function editPhoneNumber() {
    verificationCodeText.value = '__request_another_code__';
    requestedVerificationCode.value = false;
    currentStep.value = "code";
    verificationCode.value = '';
    resetAutoVerify();
    showResendCode.value = false;
    clearMessages();
}

function resendCode() {
    if (isLoading.value) {
        return;
    }
    resendCodeAttempts.value++;
    requestVerificationCode();
}

function checkAttemptsLimits() {
    if (reachedMaxAttempts.value) {
        exitPhoneVerification.value = true;
        nextStepAfterSeconds(MAX_ATTEMPTS_REDIRECT_DELAY);
    }
}

// Auto-verify: when the whole code lands at once (the keyboard's code suggestion from
// autocomplete="one-time-code", or a paste) verify it without waiting for the Verify tap. Typing
// adds one digit per input event, so someone keying the code still taps Verify and a typo can
// never use up one of the 3 attempts. A code that was sent automatically is never re-sent by
// itself: after a wrong code the person changes it and taps Verify.
const MIN_CODE_LENGTH = 4;
const WHOLE_VALUE_INPUT_TYPES = ['insertFromPaste', 'insertFromDrop', 'insertReplacementText'];
let previousCode = '';
let lastAutoVerifiedCode = '';

function onCodeInput(event) {
    errorMessage.value = '';
    const code = event?.target?.value ?? verificationCode.value;
    const arrivedWhole = WHOLE_VALUE_INPUT_TYPES.includes(event?.inputType)
        || code.length - previousCode.length >= MIN_CODE_LENGTH;
    previousCode = code;

    if (!arrivedWhole || !/^\d{4,6}$/.test(code) || code === lastAutoVerifiedCode || isLoading.value) {
        return;
    }
    lastAutoVerifiedCode = code;
    // v-model may not have caught up with this input event yet
    verificationCode.value = code;
    getVerification();
}

function resetAutoVerify() {
    previousCode = '';
    lastAutoVerifiedCode = '';
}

function validateInput(type) {
    if (type === 'phone') {
        return validatePhone();
    }
    if (type === 'code') {
        return verificationCode.value.length >= 4 && verificationCode.value.length <= 6;
    }
    return false;
}

async function smsVerificationRequest(type, data) {
    isLoading.value = true;

    const requestEndpoints = {
        'code': '/portal/phone/code',
        'verification': '/portal/phone/verification',
    };

    const apiResponse = await postAndReturnData(requestEndpoints[type] ?? requestEndpoints['code'], data);

    isLoading.value = false;
    return apiResponse;
}

function reinitializeTimer(time = COUNTDOWN_SECONDS) {
    startCountdown(time);
}

function validatePhone(finalValidation = true) {
    const phoneNumber = verificationPhoneNumber.value;

    if (app.value?.projectBrandCountry === 'US') {
        return usPhoneValidation(phoneNumber, null, finalValidation);
    } else {
        return canadianPhoneValidation(phoneNumber, null, finalValidation);
    }
}

// "(514) 111-5555" or "+1 514 111 5555" are kept as their 10 digits. The box is rewritten
// directly: when the digits don't change (a "-" typed), there is nothing new for Vue to render.
function inputPhoneNumber(event) {
    const input = event.target;
    const digits = phoneDigits(input.value);
    if (input.value !== digits) {
        input.value = digits;
    }
    verificationPhoneNumber.value = digits;
    validationMessage.value = '';
    if (!validatePhone(false)) {
        validationMessage.value = '__enter_valid_phone__';
    }
}

// --- Lifecycle ---

onMounted(async () => {
    if (!app.value.forcePhoneVerification) {
        emit('goToNextStep');
        return;
    }
    verificationPhoneNumber.value = app.value.phone_number ? app.value.phone_number : '';
    await maybeSkipForBypassNumber();
});

// Production-safe test bypass: if the entered phone is an allow-listed test number, the server tells
// us to skip SMS verification entirely (no real SMS). The allow-list stays server-side; we only ping
// it while the feature is enabled (smsBypassEnabled). For any normal number this is a no-op and the
// usual request-a-code flow runs; errors also fall back to it.
async function maybeSkipForBypassNumber() {
    if (!app.value.smsBypassEnabled || !verificationPhoneNumber.value) {
        return;
    }
    try {
        isLoading.value = true;
        const response = await postAndReturnData('/portal/phone/bypass-check', {
            phone: verificationPhoneNumber.value,
        });
        if (response && response.bypass === true) {
            successfulPhoneVerification();
        }
    } catch (e) {
        // ignore — fall back to the normal SMS verification flow
    } finally {
        isLoading.value = false;
    }
}

</script>


<style scoped>
</style>
